import React, { SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";
import { Container, Grid, TextField } from "@mui/material";
import { GroupageInterface } from "../../../../interfaces/GroupageInterface";
import SearchComponent from "../../SearchComponent";
import { InputInterface } from "../../../../interfaces/InputInterface";
import AutoSizeTextField from "../../AutoSizeTextField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { objectToQuery, requestApi } from "../../../../helpers/RequestApi";
import { GET, PATCH, POST } from "../../../../utils/MethodUtils";
import { FDOCENTETES_URL, GROUPAGE_URL } from "../../../../utils/UrlsUtils";
import { useAppSelector } from "../../../../app/hooks";
import { RootState } from "../../../../app/store";
import { toastr } from "react-redux-toastr";
import getErrorApi from "../../../../helpers/GetErrorApi";
import { FDocenteteInterface } from "../../../../interfaces/FDocenteteInterface";
import { LoadingButton } from "@mui/lab";
import notEmptyValidator from "../../../../helpers/validator/NotEmptyValidator";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled, useTheme } from "@mui/material/styles";
import { GROUPAGE_PAGE } from "../../../../utils/RouteUtils";
import { useNavigate } from "react-router-dom";
import { priceFormat } from "../../../../utils/FormatUtils";

interface State {
  initGroupage: GroupageInterface | undefined | null;
}

interface FormState {
  name: InputInterface;
  arrivedDate: InputInterface;
  departureDate: InputInterface;
  shipName: InputInterface;
  gp: InputInterface;
  fDocentetes: InputInterface;
}

interface FDocenteteOption {
  doPiece: string;
  doRef: string;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}, &.${tableCellClasses.footer}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const GroupageComponent: React.FC<State> = React.memo(({ initGroupage }) => {
  const { t, i18n } = useTranslation();
  const [groupage, setGroupage] = React.useState<
    GroupageInterface | undefined | null
  >(initGroupage);
  const [options, setOptions] = React.useState<FDocenteteOption[]>([]);
  const [loadingSave, setLoadingSave] = React.useState(false);
  const [fDocentetes, setFDocentetes] = React.useState<
    FDocenteteInterface[] | undefined
  >(undefined);
  const [loadingAutocomplete, setLoadingAutocomplete] = React.useState(false);
  const [searchFDocentete, setSearchFDocentete] = React.useState("");
  const token = useAppSelector((state: RootState) => state.globalState.token);
  const navigate = useNavigate();
  const theme = useTheme();

  const searchFDocentetes = () => {
    (async () => {
      setLoadingAutocomplete(true);
      const response = await requestApi({
        method: GET,
        path:
          FDOCENTETES_URL +
          objectToQuery({
            doType: 12,
            ...(searchFDocentete && {
              search: searchFDocentete,
            }),
          }),
        allowError: false,
        paginate: true,
        token: token,
        timeout: 60_000 * 2,
      });
      if (response.statusCode === 200) {
        setOptions(
          response.content.fDocentetes.map(
            (fDocentete: FDocenteteInterface): FDocenteteOption => {
              return {
                doPiece: fDocentete.doPiece,
                doRef: fDocentete.doRef,
              };
            }
          )
        );
      } else {
        setOptions([]);
        if (response.statusCode === 401) {
          toastr.info(t("word.info"), t("error.reconnect"));
        } else {
          for (let message of getErrorApi(response.content)) {
            toastr.error(t("word.error"), t(message));
          }
        }
      }
      setLoadingAutocomplete(false);
    })();
  };

  const handleClose = () => {
    setOptions([]);
    setSearchFDocentete("");
  };
  const getDefaultValues = React.useCallback((): FormState => {
    return {
      name: { value: groupage?.name ?? "", error: "" },
      arrivedDate: {
        value: groupage?.arrivedDate ?? "",
        error: "",
      },
      departureDate: {
        value: groupage?.departureDate ?? "",
        error: "",
      },
      shipName: {
        value: groupage?.shipName ?? "",
        error: "",
      },
      gp: { value: groupage?.gp ?? "", error: "" },
      fDocentetes: {
        value: (groupage?.fDocentetes ?? []).map(
          (doPiece: string): FDocenteteOption => {
            return {
              doPiece: doPiece,
              doRef: "",
            };
          }
        ),
        error: "",
      },
    };
  }, [groupage]);
  const [values, setValues] = React.useState<FormState>(getDefaultValues());

  const handleChange = React.useCallback(
    (prop: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues((v) => {
        let name = v.name;
        if (v.gp.value === name.value && prop === "gp") {
          name.value = event.target.value;
        }
        return {
          ...v,
          name: { ...name },
          [prop]: { ...v[prop], value: event.target.value, error: "" },
        };
      });
    },
    []
  );

  const handleChangeAutocomplete = React.useCallback(
    (prop: keyof FormState) =>
      (event: SyntheticEvent<Element, Event>, value: any) => {
        setValues((v) => {
          return {
            ...v,
            // @ts-ignore
            [prop]: { ...v[prop], value: value, error: "" },
          };
        });
      },
    []
  );

  const loadFDocentetes = React.useCallback(async () => {
    if (!groupage?.fDocentetes) {
      setFDocentetes([]);
      return;
    }
    setFDocentetes(undefined);
    const response = await requestApi({
      method: GET,
      path:
        FDOCENTETES_URL +
        objectToQuery({
          "doPiece[]": groupage?.fDocentetes,
        }),
      timeout: 60_000 * 2,
      allowError: true,
      token: token,
    });
    if (response.statusCode === 200 || response.statusCode === 201) {
      setFDocentetes(response.content.fDocentetes);
    } else if (response.statusCode === 401) {
      toastr.info(t("word.info"), t("error.reconnect"));
    } else {
      for (let message of getErrorApi(response.content)) {
        toastr.error(t("word.error"), t(message));
      }
    }
  }, [groupage?.fDocentetes, t, token]);

  const save = React.useCallback(async () => {
    const nameError = notEmptyValidator(values.name.value);
    const gpError = notEmptyValidator(values.gp.value);
    if (nameError || gpError) {
      const newValue: FormState = { ...values };
      newValue.name.error = nameError;
      newValue.gp.error = gpError;
      setValues(newValue);
      return undefined;
    }
    setLoadingSave(true);
    const isNew = !groupage?.id;
    let path = GROUPAGE_URL;
    if (!isNew) {
      path += "/" + groupage?.id;
    }
    const response = await requestApi({
      method: isNew ? POST : PATCH,
      path: path,
      allowError: true,
      token: token,
      body: {
        name: values.name.value,
        gp: values.gp.value,
        shipName: values.shipName.value,
        arrivedDate:
          values.arrivedDate.value !== "" ? values.arrivedDate.value : null,
        departureDate:
          values.departureDate.value !== "" ? values.departureDate.value : null,
        fDocentetes: values.fDocentetes.value.map((f: any) => f.doPiece),
      },
    });
    if (response.statusCode === 200 || response.statusCode === 201) {
      toastr.success(t("word.success"), t("word.success"));
      if (isNew) {
        navigate(GROUPAGE_PAGE + "/" + response.content.id);
      } else {
        setGroupage(response.content);
      }
    } else if (response.statusCode === 401) {
      toastr.info(t("word.info"), t("error.reconnect"));
    } else {
      for (let message of getErrorApi(response.content)) {
        toastr.error(t("word.error"), t(message));
      }
    }
    setLoadingSave(false);
  }, [groupage?.id, navigate, t, token, values]);

  React.useEffect(() => {
    setValues(getDefaultValues());
    loadFDocentetes();
  }, [groupage]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setGroupage(initGroupage);
  }, [initGroupage]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const timeoutTyping = setTimeout(() => {
      searchFDocentetes();
    }, 500);
    return () => clearTimeout(timeoutTyping);
  }, [searchFDocentete.toString().trim()]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Container maxWidth="xl">
        {groupage === undefined ? (
          <SearchComponent nbColumn={1} nbLines={1} />
        ) : (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              {(["name", "gp", "shipName"] as (keyof FormState)[]).map(
                (property) => (
                  <Box
                    key={property}
                    sx={{
                      marginBottom: 2,
                    }}
                  >
                    <AutoSizeTextField
                      autoComplete="off"
                      error={!!values[property].error}
                      helperText={t(values[property].error ?? "")}
                      type="text"
                      value={values[property].value}
                      onChange={handleChange(property)}
                      label={t("field." + property)}
                      sx={{
                        "& .MuiInputBase-input": {
                          minWidth: `200px`,
                        },
                      }}
                    />
                  </Box>
                )
              )}
              {(["arrivedDate", "departureDate"] as (keyof FormState)[]).map(
                (property) => (
                  <Box
                    key={property}
                    sx={{
                      marginBottom: 2,
                    }}
                  >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label={t("field." + property)}
                        value={dayjs(values[property].value)}
                        onChange={(newValue) => {
                          setValues((v) => ({
                            ...v,
                            [property]: {
                              ...v[property],
                              value: newValue?.format("YYYY-MM-DD") ?? "",
                            },
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            error={false}
                            label={t("field." + property)}
                            sx={{ maxWidth: "220px" }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Box>
                )
              )}
              <Autocomplete
                onOpen={searchFDocentetes}
                onClose={handleClose}
                multiple={true}
                disableCloseOnSelect={true}
                onChange={handleChangeAutocomplete("fDocentetes")}
                value={values.fDocentetes.value}
                isOptionEqualToValue={(
                  option: FDocenteteOption,
                  value: FDocenteteOption
                ) => option.doPiece === value.doPiece}
                getOptionLabel={(option) => (option.doPiece)}
                filterOptions={(opts, state) =>
                  opts.filter((opt) => {
                    const search = state.inputValue.toLowerCase();
                    return (
                      opt.doPiece.toLowerCase().includes(search) ||
                      (opt.doRef ?? "").toLowerCase().includes(search)
                    );
                  })
                }
                renderOption={(props, option) => {
                  return (
                    <li
                      {...props}
                      key={option.doPiece}
                      style={{ display: "block" }}
                    >
                  <span style={{ color: theme.palette.primary.main }}>
                    {"[" + option.doRef + "] "}
                  </span>
                      {option.doPiece}
                    </li>
                  );
                }}
                options={options}
                loading={loadingAutocomplete}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("word.fDocentetes")}
                    value={searchFDocentete}
                    onChange={(e) => {
                      setSearchFDocentete(e.target.value);
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAutocomplete ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
        <Box sx={{ marginY: 2, textAlign: "right" }}>
          <LoadingButton
            variant="contained"
            onClick={save}
            loading={loadingSave}
            disabled={!groupage}
          >
            {t("word.save")}
          </LoadingButton>
        </Box>
        {fDocentetes ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>{t("column.doPiece")}</StyledTableCell>
                  <StyledTableCell>{t("field.arrivedDate")}</StyledTableCell>
                  <StyledTableCell>{t("field.gp")}</StyledTableCell>
                  <StyledTableCell>{t("word.amount")}</StyledTableCell>
                  <StyledTableCell>{t("word.doStatut.label")}</StyledTableCell>
                  <StyledTableCell>{t("word.reference")}</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {fDocentetes.map((fDocentete, indexFDocentete) => (
                  <StyledTableRow key={indexFDocentete}>
                    <StyledTableCell>{fDocentete.doPiece}</StyledTableCell>
                    <StyledTableCell>
                      {new Date(fDocentete.doDatelivr).toLocaleString(
                        i18n.language,
                        {
                          dateStyle: "long",
                          timeZone: "Etc/UTC",
                        }
                      )}
                    </StyledTableCell>
                    <StyledTableCell>{fDocentete.doCoord01}</StyledTableCell>
                    <StyledTableCell>
                      {priceFormat(
                        fDocentete.doTotalttc * 100,
                        i18n.language,
                        "EUR"
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {fDocentete.doStatutString}
                    </StyledTableCell>
                    <StyledTableCell>{fDocentete.doRef}</StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <SearchComponent nbColumn={1} nbLines={1} />
        )}
      </Container>
    </>
  );
});

export default GroupageComponent;
